import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { createTranslator } from '../../i18n';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CalendarEventCardProps {
  calendarEventId: string;
  content?: string;
  isOutgoing?: boolean;
}

type EventStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
const EVENT_STATUS_VALUES: EventStatus[] = ['scheduled', 'in_progress', 'completed', 'cancelled'];

export const CalendarEventCard: React.FC<CalendarEventCardProps> = ({
  calendarEventId,
  content,
  isOutgoing = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const eventStatusLabels: Record<EventStatus, string> = {
    scheduled: t('card.statusScheduled'),
    in_progress: t('card.statusInProgress'),
    completed: t('card.statusCompleted'),
    cancelled: t('card.statusCancelled'),
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!calendarEventId) {
        setLoading(false);
        return;
      }
      try {
        const res = await apiService.getEventById(calendarEventId);
        if (!cancelled && res?.data) setEvent(res.data);
      } catch (_) {
        if (!cancelled) setEvent(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [calendarEventId]);

  const handleViewEvent = () => {
    navigateTo('event-detail', undefined, { eventId: calendarEventId });
  };

  const handleChangeStatus = () => {
    if (!event) return;
    Alert.alert(
      t('card.eventStatusTitle'),
      t('card.selectNewStatus'),
      [
        ...EVENT_STATUS_VALUES.map((value) => ({
          text: eventStatusLabels[value],
          onPress: () => updateEventStatus(value),
        })),
        { text: t('card.cancel'), style: 'cancel' },
      ]
    );
  };

  const updateEventStatus = async (eventStatus: 'scheduled' | 'in_progress' | 'completed' | 'cancelled') => {
    if (!calendarEventId) return;
    setUpdating(true);
    try {
      await apiService.updateEvent(calendarEventId, { eventStatus });
      showNotification('success', t('card.statusUpdated'), t('card.eventStatusUpdated', { status: eventStatus }));
      const res = await apiService.getEventById(calendarEventId);
      if (res?.data) setEvent(res.data);
    } catch (e: any) {
      showNotification('error', t('card.updateFailed'), e?.message || t('card.updateEventFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const cardStyles = isOutgoing ? styles.cardOutgoing : styles.cardIncoming;
  const textStyles = isOutgoing ? styles.textOutgoing : styles.textIncoming;

  if (loading) {
    return (
      <View style={[styles.card, cardStyles]}>
        <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.primary} />
        <Text style={[styles.label, textStyles]}>{t('card.calendarEvent')}</Text>
      </View>
    );
  }

  const startStr = event?.startDate
    ? new Date(event.startDate).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : null;
  const endStr = event?.endDate
    ? new Date(event.endDate).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <View style={[styles.card, cardStyles]}>
      <View style={styles.header}>
        <Ionicons name="calendar-outline" size={20} color={isOutgoing ? chat.out.accent : colors.primary} />
        <Text style={[styles.title, textStyles]}>
          {event?.title || t('card.calendarEvent')}
        </Text>
      </View>
      {content ? (
        <Text style={[styles.content, textStyles]} numberOfLines={2}>
          {content}
        </Text>
      ) : null}
      {event ? (
        <View style={styles.meta}>
          {startStr && (
            <Text style={[styles.metaText, textStyles]}>
              {t('card.eventStart')}{startStr}
            </Text>
          )}
          {endStr && (
            <Text style={[styles.metaText, textStyles]}>
              {t('card.eventEnd')}{endStr}
            </Text>
          )}
          {event.eventStatus && (
            <Text style={[styles.metaText, textStyles]}>
              {t('card.eventStatus')}{eventStatusLabels[event.eventStatus as EventStatus] ?? event.eventStatus}
            </Text>
          )}
        </View>
      ) : null}
      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
          onPress={handleViewEvent}
          disabled={updating}
        >
          <Ionicons name="eye-outline" size={16} color={isOutgoing ? chat.out.accent : colors.white} />
          <Text style={[styles.actionLabel, { color: isOutgoing ? chat.out.accent : colors.white }]}>
            {t('card.viewEvent')}
          </Text>
        </TouchableOpacity>
        {event ? (
          <TouchableOpacity
            style={[styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
            onPress={handleChangeStatus}
            disabled={updating}
          >
            {updating ? (
              <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.white} />
            ) : (
              <>
                <Ionicons name="list-outline" size={16} color={isOutgoing ? chat.out.accent : colors.white} />
                <Text style={[styles.actionLabel, { color: isOutgoing ? chat.out.accent : colors.white }]}>
                  {t('card.statusTitle')}
                </Text>
              </>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    maxWidth: '92%',
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  label: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
  },
  content: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    opacity: 0.95,
  },
  meta: {
    marginBottom: spacing.sm,
  },
  metaText: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
  },
  actionBtnOutgoing: {
    backgroundColor: chat.out.chip,
  },
  actionBtnIncoming: {
    backgroundColor: colors.primary,
  },
  actionLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
});
